import { applyMiddleware, combineReducers, compose, createStore } from 'redux';
import createSagaMiddleware from 'redux-saga';

import { isNotChrome } from 'Lib/util';
import { omit } from 'Lib/utils/lodashUtils';

import { ENABLE_REDUX_DEVTOOLS } from './app/constants';
import { dynamicReducerComplete } from './dynamicLoading/actions';
import dynamicReducer from './dynamicLoading/ReducerRegistry';
import { createAsyncReducerState, registerAsyncBundles } from './asyncBundle';
import { rootReducerShape } from './rootReducer';
import rootSaga from './rootSaga';

const composeEnhancers =
  ENABLE_REDUX_DEVTOOLS && __CLIENT__ && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
        shouldHotReload: false,
        stateSanitizer: (state) => omit(state, ['intlMessages']),
      })
    : compose;

/**
 * Creates the store and middleware
 *
 * @param  {Object} [initialState] - initial Redux state
 * @param  {Object} [options] - configuration options
 * @param  {Object} [options.useLoggerMiddleware] - whether to include redux-logger
 * @return {Object} store and sagaMiddleware
 */
export function createStoreAndMiddleware(
  initialState,
  { onReplaceReducer, useLoggerMiddleware = __DEVELOPMENT__ && isNotChrome() } = {}
) {
  const sagaMiddleware = createSagaMiddleware({
    onError: (error, { sagaStack }) => {
      if (__DEVELOPMENT__) {
        console.error(error, sagaStack);
      }
      // throw error to send out event
      throw error;
    },
  });
  const middleware = [sagaMiddleware];

  // for easier debugging outside of Chrome
  if (__CLIENT__ && useLoggerMiddleware) {
    const { logger } = require('redux-logger');
    middleware.push(logger);
  }

  const enhancers = composeEnhancers(
    applyMiddleware(...middleware)
    // other store enhancers if any
  );

  // There should be no overlap between rootReducerShape and asyncReducers
  const asyncReducers = createAsyncReducerState(initialState);
  const initialReducers = { ...rootReducerShape, ...asyncReducers };

  const store = createStore(combineReducers(initialReducers), initialState, enhancers);

  dynamicReducer.subscribe((reducers) => {
    // reducers === asyncReducers
    store.replaceReducer(combineReducers({ ...initialReducers, ...reducers }));
    if (onReplaceReducer) {
      store.dispatch(dynamicReducerComplete(Object.keys(reducers)));
      onReplaceReducer(store);
    }
  });

  if (__CLIENT__) {
    registerAsyncBundles();
  }

  return {
    sagaMiddleware,
    store,
  };
}

/**
 * Creates the store and middleware and runs the root saga (on the client)
 *
 * @param  {Object} [initialState] - initial Redux state
 * @param  {Object} [options] - additional options passed to createStoreAndMiddleware
 * @return {Store} Redux store
 */
export default function configureStore(initialState, options) {
  const { sagaMiddleware, store } = createStoreAndMiddleware(initialState, options);

  if (__CLIENT__) {
    sagaMiddleware.run(rootSaga);
  }

  return store;
}
